import { useEffect, useState, useCallback, ReactChild, ReactFragment, ReactPortal } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { colors, breakpoints, fontWeights } from '@/styles'
import { LockTransparent, ArrowTransparent } from '@/components/icons'
import useMediaQuery from '@/hooks/use-media-query'

type HandleNavLinkClick = (key: string) => void
type GetContractButton = () => boolean | ReactChild | ReactFragment | ReactPortal | null | undefined

interface MobileMenuProps {
  location: { pathname: string | string[] }
  handleNavLinkClick: HandleNavLinkClick
  isLocked: boolean
  isScrolling: boolean
}

interface BottomNavWrapperProps {
  currentStep: number
}

interface BottomNavItemWrapperProps {
  left?: boolean
  right?: boolean
}

const MobileMenuWrapper = styled.div`
  white-space: nowrap;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  -ms-overflow-style: -ms-autohiding-scrollbar;
  scrollbar-width: none;
  width: 100%;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  height: 44px;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    width: unset;
    display: flex;
    align-items: flex-end;
    grid-template-columns: unset;
    padding: 0 10px;
  }

  ::-webkit-scrollbar {
    display: none;
  }

  font-size: 14px;
  color: ${colors.grey56};

  span {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    padding: 10px;
    height: 30px;
    cursor: pointer;
    height: 44px;

    @media screen and (max-width: ${breakpoints.phoneMax}) {
      justify-content: unset;
      align-items: flex-end;
      padding: 10px;
      margin: auto 10px;
    }

    &.selected {
      color: ${colors.black};
      border-bottom: 2px solid ${colors.red64};
      padding-bottom: 8px;
    }
  }
`

const MobileMenuItemWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  pointer-events: none;

  svg {
    width: 11px;
  }
`

const BottomNavWrapper = styled.div<BottomNavWrapperProps>`
  display: flex;
  width: 100%;
  justify-content: space-between;
  margin-top: 46px;
  ${props => props.currentStep === 0 && `justify-content: end;`}
`

const MobileBottomNavWrapper = styled.div<BottomNavWrapperProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  max-width: 900px;
  border: 1.5px solid ${colors.grey88};
  border-radius: 6px;
  background-color: ${colors.white};
  height: 48px;
  cursor: pointer;
`

const ArrowTransparentStyled = styled(ArrowTransparent)`
  width: 12px;
  height: 12px;
`

const ArrowTransparentStyledRight = styled(ArrowTransparentStyled)`
  transform: rotate(180deg);
`

const BottomNavItemWrapper = styled.div<BottomNavItemWrapperProps>`
  display: block;
  color: ${colors.blue32};
  cursor: pointer;
  font-weight: ${fontWeights.medium};

  ${props => props.left && `svg { margin-right: 10px; }`}
  ${props => props.right && `svg { margin-left: 10px; }`}
`

const MobileBottomNavItemWrapper = styled.div`
  display: block;
  font-size: 18px;
  font-weight: 700;
  color: ${colors.blue32};
`

const StepLabel = styled.span`
  font-weight: bold;
`

const useMenus = () => {
  const { t } = useTranslation()
  return [
    {
      key: 'trust-score',
      label: t('r.liv_score_card.view.current_score.title', 'Trust Score'),
      clickId: 'application-trust-score-sections-score'
    },
    {
      key: 'basic-information',
      label: t('r.profile.edit.basic_information.title', 'Basic Information'),
      clickId: 'application-trust-score-sections-basic-info'
    },
    {
      key: 'references',
      label: t('r.profile.edit.references.tab', 'References'),
      clickId: 'application-trust-score-sections-reference'
    },
    {
      key: 'documents',
      label: t('l.contract_edit.application_contract_edit.documents.button', 'Documents'),
      clickId: 'application-trust-score-sections-docs'
    }
  ]
}

export const MobileMenu: React.FC<MobileMenuProps> = ({
  location,
  isLocked,
  handleNavLinkClick
}) => {
  const menus = useMenus()

  const handleMobileNav = useCallback(
    (node: HTMLElement | null) => {
      const selectedItem = node?.querySelector('.selected')
      if (!selectedItem) return

      const selectedItemWidth = selectedItem.getBoundingClientRect().width
      const selectedItemX = selectedItem.getBoundingClientRect().x
      const menuWidth = node?.getBoundingClientRect().width
      const scrollLeft = node?.scrollLeft || 0

      let scrollToX = selectedItemX + selectedItemWidth * 2 - menuWidth! + scrollLeft
      if (scrollToX < 0) {
        scrollToX = 0
      }

      node?.scrollTo({
        left: scrollToX,
        behavior: 'smooth'
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location]
  )

  return (
    <MobileMenuWrapper ref={handleMobileNav}>
      {menus.map(({ key, label, clickId }, index) => {
        const current = location.pathname.includes(key)
        const handleClick = (e: React.MouseEvent) => {
          e.stopPropagation()
          handleNavLinkClick(key)
        }
        return (
          <span key={key} id={clickId} className={current ? 'selected' : ''} onClick={handleClick}>
            <MobileMenuItemWrapper>
              {label}
              {index === 0 && isLocked && (
                <LockTransparent fill={current ? colors.black : colors.grey56} />
              )}
            </MobileMenuItemWrapper>
          </span>
        )
      })}
    </MobileMenuWrapper>
  )
}

interface BottomNavigationProps {
  handleNavLinkClick: HandleNavLinkClick
  getContractButton: GetContractButton
  left: boolean
  right: boolean
}

export const BottomNavigation: React.FC<BottomNavigationProps> = ({
  handleNavLinkClick,
  getContractButton
}) => {
  const [currentStep, setCurrentStep] = useState<number>(0)
  const [nextStepLabel, setNextStepLabel] = useState<string>('')
  const [prevStepLabel, setPrevStepLabel] = useState<string>('')
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.desktopSmall})`)

  const menus = useMenus()

  useEffect(() => {
    const currentPath = window.location.pathname.split('/').pop()
    const currentStepIndex = menus.findIndex(menu => menu.key === currentPath)
    setCurrentStep(currentStepIndex >= 0 ? currentStepIndex : 0)
  }, [menus])

  useEffect(() => {
    if (currentStep > 0) {
      setPrevStepLabel(menus[currentStep - 1].label ?? '')
    }
    if (currentStep < menus.length - 1) {
      setNextStepLabel(menus[currentStep + 1].label ?? '')
    }
  }, [currentStep, menus])

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
      const currentPath = menus[currentStep - 1].key
      handleNavLinkClick(currentPath)
    }
  }

  const handleForward = () => {
    if (currentStep < menus.length - 1) {
      setCurrentStep(currentStep + 1)
      const currentPath = menus[currentStep + 1].key
      handleNavLinkClick(currentPath)
    }
  }

  return (
    <>
      {isMobile ? (
        currentStep < menus.length - 1 && (
          <MobileBottomNavWrapper currentStep={currentStep} onClick={handleForward}>
            <MobileBottomNavItemWrapper>
              <span>{`Next: ${nextStepLabel}`}</span>
            </MobileBottomNavItemWrapper>
          </MobileBottomNavWrapper>
        )
      ) : (
        <BottomNavWrapper currentStep={currentStep}>
          {currentStep > 0 && (
            <BottomNavItemWrapper left>
              <ArrowTransparentStyled fill={colors.blue32} />{' '}
              <StepLabel onClick={handleBack}>{prevStepLabel}</StepLabel>
            </BottomNavItemWrapper>
          )}
          {currentStep < menus.length - 1 ? (
            <BottomNavItemWrapper right>
              <StepLabel onClick={handleForward}>{nextStepLabel}</StepLabel>
              <ArrowTransparentStyledRight fill={colors.blue32} />
            </BottomNavItemWrapper>
          ) : (
            <span onClick={handleForward}>{getContractButton()}</span>
          )}
        </BottomNavWrapper>
      )}
    </>
  )
}
